/**
 * SPEC-AgentUI — pure helpers for the Agent UI sub-thread.
 *
 * Lives in `client/src/lib/` so the rendering components and the unit tests
 * share ONE source of truth for the small but failure-prone decisions the
 * Agent UI makes. Without these extractions, the components could pass
 * mocked-prop unit tests while still rendering the wrong thing at wire
 * time — the exact failure mode the `spec-implement` skill exists to catch.
 *
 * Tests: `client/src/lib/__tests__/agent-ui-helpers.test.ts`.
 *
 * What this module covers (each lives behind one named export):
 *
 *   • computeTimerState(secondsRemaining)        → "healthy" | "warning" | "critical"
 *   • computeTimerLabel(secondsRemaining, state) → "0:42" / "0:14 — releasing soon" / "0:07"
 *   • notifyCustomerLabel(customer)              → "Notify CookUnity" etc.
 *   • notifyCustomerListChip(customer)           → "CU notified" / "Aramark notified"
 *   • buildLookupUrl(query)                      → "/api/qa/lookup?q=…" or null
 *   • buildDecisionBody(input)                   → typed body for POST /decision
 *   • decisionBodyError(input)                   → string | null — UI-side guard
 *   • decideTimerSurface(claim, currentPageId)   → "inline" | "toast" | "hidden"
 *   • isClaimSuppressed(id, sessionStorage)      → boolean
 *   • suppressClaim(id, sessionStorage)          → void (writes the sessionStorage key)
 *   • agentRouteRedirect(role, route)            → string | null — Wouter redirect target
 *
 * Every named export is referenced from `client/src/pages/agent-list-v1.tsx`,
 * `client/src/pages/agent-detail-v1.tsx`, and the timer / decision-bar /
 * resolution-panel components in `client/src/components/qa/`. The grep-style
 * lint test in `client/src/lib/__tests__/agent-ui-lint.test.ts` guarantees
 * that no Agent surface reaches for an AI field directly.
 */

import type { Role } from "./role-helpers";

// ─────────────────────────────────────────────────────────────────────────
// Timer state — drives the inline TimerChip + ClaimToast surfaces.
//
// SPEC-AgentDetail-Revisions collapses the old three-tier model to two
// non-expired states:
//   ≤ 15s          → warning  (red pulse + "— Expiring Soon" suffix)
//   otherwise      → healthy  (purple chip)
//   ≤ 0 / null     → expired  (the surface should hide; the subscription
//                              provider does NOT push a "released" toast
//                              anymore — agents discover loss on their next
//                              queue glance)
// The "critical" tier is gone. Warning carries the full alarm.
// ─────────────────────────────────────────────────────────────────────────

export type TimerState = "healthy" | "warning" | "expired";

export const TIMER_WARNING_S = 15;

export function computeTimerState(secondsRemaining: number | null | undefined): TimerState {
  if (secondsRemaining === null || secondsRemaining === undefined) return "expired";
  if (!Number.isFinite(secondsRemaining) || secondsRemaining <= 0) return "expired";
  if (secondsRemaining <= TIMER_WARNING_S) return "warning";
  return "healthy";
}

export function computeTimerLabel(secondsRemaining: number | null | undefined): string {
  if (secondsRemaining === null || secondsRemaining === undefined || !Number.isFinite(secondsRemaining) || secondsRemaining <= 0) {
    return "0:00";
  }
  const whole = Math.max(0, Math.floor(secondsRemaining));
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  const base = `${m}:${s.toString().padStart(2, "0")}`;
  // SPEC-AgentDetail-Revisions §A6 — warning suffix is "— Expiring Soon".
  // No critical-tier terse-format branch (a 7s sample reads identical to
  // a 14s sample — both warning).
  if (whole <= TIMER_WARNING_S) return `${base} — Expiring Soon`;
  return base;
}

// ─────────────────────────────────────────────────────────────────────────
// Notify Customer — dynamic label per customer.
//   CookUnity → "Notify CookUnity"
//   Aramark   → "Notify Aramark"
//   null      → "Notify customer"        (no customer attached)
// The list-chip variant ("CU notified" / "Aramark notified") is what the
// Resolved-Good-CU-pending row shows in *Needs resolution*.
// ─────────────────────────────────────────────────────────────────────────

export function notifyCustomerLabel(customer: string | null | undefined): string {
  const trimmed = (customer ?? "").trim();
  if (!trimmed) return "Notify customer";
  return `Notify ${trimmed}`;
}

export function notifyCustomerListChip(customer: string | null | undefined): string {
  const trimmed = (customer ?? "").trim();
  if (!trimmed) return "Customer notified";
  // The Slack tradition uses "CU" specifically for CookUnity. Match the
  // mockup wording on the list chip ("CU notified" not "CookUnity notified").
  if (/^cookunity$/i.test(trimmed)) return "CU notified";
  return `${trimmed} notified`;
}

// ─────────────────────────────────────────────────────────────────────────
// Lookup URL — debounced query string builder. Returns null when the input
// is empty or shorter than the 2-char floor (matches the mockup hint).
// ─────────────────────────────────────────────────────────────────────────

export const LOOKUP_MIN_CHARS = 2;

export function buildLookupUrl(query: string | null | undefined): string | null {
  const q = (query ?? "").trim();
  if (q.length < LOOKUP_MIN_CHARS) return null;
  return `/api/qa/lookup?q=${encodeURIComponent(q)}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Decision body — what the resolution panel sends to POST /decision.
// Both `buildDecisionBody` and `decisionBodyError` are pure: the panel
// renders disabled Save until `decisionBodyError(input) === null`.
// ─────────────────────────────────────────────────────────────────────────

export const BAD_REASONS_UI = ["mistake", "cant_verify", "bag_notes", "damage", "other"] as const;
export type BadReasonUi = (typeof BAD_REASONS_UI)[number];

export const INITIAL_ACTIONS_UI = ["phone", "text", "other"] as const;
export type InitialActionUi = (typeof INITIAL_ACTIONS_UI)[number];

export interface DecisionInput {
  // SKIP added 2026-05-31 — bug fix. The Skip button was previously casting
  // its input as `any` at the call site, which let the SKIP value fall
  // through to the BAD branch of buildDecisionBody and silently rewrite
  // the body to `{ decision: "BAD", badReason: undefined, ... }`. Server
  // rejected with `400: badReason required for BAD`. Including SKIP in the
  // union forces the type checker to demand SKIP-aware logic.
  decision: "GOOD" | "BAD" | "SKIP";
  badReason?: BadReasonUi | null;
  badReasonOther?: string | null;
  initialAction?: InitialActionUi | null;
  initialActionOther?: string | null;
  initialNote?: string | null;
  /** Training Simulator (2026-09-15): Phases 3–4 hide Step 2, so the action is neither asked nor required. Live agents always require it. */
  initialActionRequired?: boolean;
  /** Training Simulator (2026-09-15): a Bad decision must say why in the note. Live agents keep the note optional. */
  noteRequired?: boolean;
  notifyCustomer?: boolean;
  // SPEC-SeniorConfirm §5.3 — Agree vs Disagree discriminator.
  // true  = the Senior is explicitly Disagreeing (dock Good/Bad/Skip press when
  //         agentDecision exists; always Disagree even on same-verdict press).
  // false = the Senior is explicitly Agreeing (thumbs-up; no override, no notify).
  // undefined = first-decision (no agentDecision) or agent submitting; server
  //             treats missing/undefined the same as false for non-senior callers.
  disagree?: boolean;
}

export interface DecisionBody {
  decision: "GOOD" | "BAD" | "SKIP";
  badReason?: BadReasonUi;
  badReasonOther?: string;
  initialAction?: InitialActionUi;
  initialActionOther?: string;
  initialNote?: string;
  notifyCustomer?: boolean;
  // SPEC-SeniorConfirm §5.3 — wire discriminator for POST /decision
  disagree?: boolean;
}

export function decisionBodyError(input: DecisionInput): string | null {
  if (input.decision === "GOOD") return null;
  // SKIP submits immediately — no companion fields required or allowed.
  if (input.decision === "SKIP") return null;
  // BAD path — both pickers required, Other variants require their string.
  if (!input.badReason) return "Pick a reason";
  if (input.badReason === "other" && !(input.badReasonOther ?? "").trim()) return "Describe the reason";
  if (input.noteRequired && !(input.initialNote ?? "").trim()) return "Say why it is Bad";
  if (input.initialActionRequired === false) return null;
  if (!input.initialAction) return "Pick an action";
  if (input.initialAction === "other" && !(input.initialActionOther ?? "").trim()) return "Describe the action";
  return null;
}

export function buildDecisionBody(input: DecisionInput): DecisionBody {
  if (input.decision === "GOOD") {
    const out: DecisionBody = { decision: "GOOD" };
    if (input.notifyCustomer) out.notifyCustomer = true;
    if ((input.initialNote ?? "").trim()) out.initialNote = (input.initialNote ?? "").trim();
    // SPEC-SeniorConfirm §5.3 — pass disagree only when explicitly set, so
    // agent submissions (which never set the field) stay unaffected.
    if (input.disagree === true) out.disagree = true;
    if (input.disagree === false) out.disagree = false;
    return out;
  }
  // SKIP — server-side validation rejects any companion field (badReason /
  // initialAction / initialNote / notifyCustomer). Always emit a clean,
  // solo body. See server/routes/agentUi.ts §"SKIP cannot carry".
  if (input.decision === "SKIP") {
    return { decision: "SKIP" };
  }
  // BAD — assume caller already gated on decisionBodyError().
  const out: DecisionBody = {
    decision: "BAD",
    badReason: input.badReason!,
    initialAction: input.initialAction!,
  };
  if (input.badReason === "other") out.badReasonOther = (input.badReasonOther ?? "").trim();
  if (input.initialAction === "other") out.initialActionOther = (input.initialActionOther ?? "").trim();
  if ((input.initialNote ?? "").trim()) out.initialNote = (input.initialNote ?? "").trim();
  if (input.notifyCustomer) out.notifyCustomer = true;
  // SPEC-SeniorConfirm §5.3
  if (input.disagree === true) out.disagree = true;
  if (input.disagree === false) out.disagree = false;
  return out;
}

// ─────────────────────────────────────────────────────────────────────────
// Timer surface routing — inline chip vs background toast.
//
// Inline = Linda is currently on the claim's detail page.
// Toast  = some OTHER claim crossed a warning threshold; surface a toast
//          to the top-right.
// Hidden = the claim has been suppressed via "Let it go" this shift, OR the
//          claim is already expired.
// ─────────────────────────────────────────────────────────────────────────

export type TimerSurface = "inline" | "toast" | "hidden";

export interface ClaimSurfaceInput {
  claimDeliveryId: string;
  currentPageDeliveryId: string | null;
  state: TimerState;
  suppressed: boolean;
}

export function decideTimerSurface(input: ClaimSurfaceInput): TimerSurface {
  if (input.suppressed) return "hidden";
  if (input.state === "expired") return "hidden";
  if (input.state === "healthy") {
    // Only the inline surface is interested in healthy timers; toasts are for
    // background claims and only fire on the 30s + 10s thresholds.
    return input.currentPageDeliveryId === input.claimDeliveryId ? "inline" : "hidden";
  }
  // Warning — escalate. (Critical tier removed per SPEC-AgentDetail-Revisions.)
  if (input.currentPageDeliveryId === input.claimDeliveryId) return "inline";
  return "toast";
}

// ─────────────────────────────────────────────────────────────────────────
// "Let it go" suppression — sessionStorage key tracks claim ids the agent
// chose to let expire silently this shift. The toast subscription provider
// adds the id when the agent taps the secondary button; subsequent warnings
// for the same claim id stay hidden.
// ─────────────────────────────────────────────────────────────────────────

export const SUPPRESSED_CLAIMS_KEY = "suppressedClaims";

export interface MinimalStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function readSuppressedClaims(storage: MinimalStorage): Set<string> {
  const raw = storage.getItem(SUPPRESSED_CLAIMS_KEY);
  if (!raw) return new Set();
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set(parsed.filter((x) => typeof x === "string"));
  } catch (_e) { /* drop malformed */ }
  return new Set();
}

export function isClaimSuppressed(claimId: string, storage: MinimalStorage): boolean {
  return readSuppressedClaims(storage).has(claimId);
}

export function suppressClaim(claimId: string, storage: MinimalStorage): void {
  const set = readSuppressedClaims(storage);
  set.add(claimId);
  storage.setItem(SUPPRESSED_CLAIMS_KEY, JSON.stringify(Array.from(set)));
}

// ─────────────────────────────────────────────────────────────────────────
// Route protection — the SPEC's locked architectural rule:
//
//   /qa/agent requires role === 'agent'.
//
// SPEC-UnifiedQADetail Phase 2 — the agent detail route is unified onto
// `/qa/delivery/:id` (the senior detail route). The mode (agent vs senior
// layout) is now selected by `useViewMode()` per-user; agents are pinned to
// agent view, seniors + admins can toggle. A one-cycle Redirect at
// `/qa/agent/delivery/:id` keeps legacy bookmarks working.
//
// A senior typing `/qa/agent` (the list) gets redirected to the senior-tier
// list so the agent list never becomes a senior view-mode flag. Admins
// (role 'admin') redirect to '/' rather than the senior list because the
// SPEC leaves admin-on-agent-routes undefined; '/' is the safe landing.
// ─────────────────────────────────────────────────────────────────────────

export type AgentRoute =
  | { kind: "list" }
  | { kind: "detail"; deliveryId: string };

export function agentRouteRedirect(role: Role | null, route: AgentRoute): string | null {
  if (role === "agent") return null;
  // SPEC-TrainingSimulator §3 (D16) — a trainee is served /train only.
  if (role === "trainee") return "/train";
  if (role === "senior") {
    // SPEC says "/qa" — `/qa/list` is the senior QA list route in
    // `App.tsx`, which is what "the senior dashboard" means in practice
    // until the spec carves out a dedicated senior dashboard surface.
    if (route.kind === "list") return "/qa/list";
    return `/qa/delivery/${route.deliveryId}`;
  }
  // admin / bot / null — bounce to home; AdminRoute would handle deeper
  // surfaces separately.
  return "/";
}

/**
 * SPEC-TrainingSimulator §3 — where the app shell sends a trainee who lands
 * anywhere outside /train. `null` when no redirect is needed (not a trainee,
 * or already on a /train route). Pure so App.tsx and the tests share it.
 */
export function traineeRedirectTarget(role: Role | null, location: string): string | null {
  if (role !== "trainee") return null;
  if (location === "/train" || location.startsWith("/train/")) return null;
  return "/train";
}

// ─────────────────────────────────────────────────────────────────────────
// Resolution log — the "what does the panel render" classifier. Returns
// the state the panel uses to pick its layout: the initial step-1+step-2
// form when no decision has been written, the post-save iterable log
// thereafter.
// ─────────────────────────────────────────────────────────────────────────

export type ResolutionPanelState =
  | { kind: "hidden" }                     // pre-Bad — agent hasn't tapped Bad yet
  | { kind: "form" }                       // Bad tapped, no save yet
  | { kind: "log"; canResolve: boolean };

export interface ResolutionPanelInput {
  // BAD_RESOLVED is the caller-side name for bad + resolution 'resolved' — the
  // terminal bad state. (The retired BAD_FINAL decision used to sit here.)
  decision: "GOOD" | "BAD" | "BAD_RESOLVED" | null | undefined;
  badPanelOpen: boolean;
}

export function deriveResolutionPanelState(input: ResolutionPanelInput): ResolutionPanelState {
  if (input.decision === "BAD") return { kind: "log", canResolve: true };
  if (input.decision === "BAD_RESOLVED") return { kind: "log", canResolve: false };
  if (input.badPanelOpen) return { kind: "form" };
  return { kind: "hidden" };
}

// ─────────────────────────────────────────────────────────────────────────
// Open with me — the list-view section showing the agent's active claims.
//
// `secondsUntilExpiry` is a wall-clock-driven helper: claim payloads carry
// `expiresAt` as an ISO string (from `/api/qa/claims/active`), and the row
// uses it to compute the live countdown each render tick.
//
// `isExpiringRow` returns true when the timer state is critical OR the row
// just passed the warning boundary — drives the Frame 4g inline EXPIRING
// annotation (red left border + "EXPIRING" chip). Critical is the trigger
// (matches the mockup caption "<10s").
// ─────────────────────────────────────────────────────────────────────────

export function secondsUntilExpiry(expiresAt: string | null | undefined, now: number = Date.now()): number | null {
  if (!expiresAt) return null;
  const t = Date.parse(expiresAt);
  if (!Number.isFinite(t)) return null;
  return Math.max(-1, (t - now) / 1000);
}

export function isExpiringRow(state: TimerState): boolean {
  // SPEC-AgentDetail-Revisions §"Timer + toast" — critical tier removed.
  // Warning state owns the "EXPIRING" surface (the list-row red border + chip).
  return state === "warning";
}

// ─────────────────────────────────────────────────────────────────────────
// Needs resolution — the list-view section that surfaces deliveries the
// agent (or anyone) needs to follow up on.
//
// Two row variants:
//   • bad-in-progress       — qa_decision === 'BAD' (red left border)
//   • cu-pending            — qa_decision === 'GOOD' AND cu_notified_at set
//                             (orange left border, "✉ Notify [Customer]" pill)
//
// `classifyNeedsResolutionRow` is the seam between the server payload and
// the row variant. It returns null when the row doesn't match either bucket
// — the caller filters those out so the section is hidden cleanly when
// empty.
// ─────────────────────────────────────────────────────────────────────────

export type NeedsResolutionVariant = "bad-in-progress" | "cu-pending";

export interface NeedsResolutionRowInput {
  qaDecision: string | null | undefined;
  cuNotifiedAt: string | null | undefined;
}

export function classifyNeedsResolutionRow(row: NeedsResolutionRowInput): NeedsResolutionVariant | null {
  const decision = (row.qaDecision ?? "").toUpperCase();
  if (decision === "BAD") return "bad-in-progress";
  if (decision === "GOOD" && row.cuNotifiedAt) return "cu-pending";
  return null;
}
